import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip, Stack,
  LinearProgress, CircularProgress, Divider, Backdrop, Alert,
} from '@mui/material';
import {
  PlayArrow as PlayIcon, SkipNext as StepIcon, Pause as PauseIcon,
  Stop as StopIcon, FiberManualRecord as RecordIcon,
  Extension as ExtensionIcon, Settings as SettingsIcon,
  MoreVert as MoreIcon, AutoAwesome as AiIcon,
  Mouse as MouseIcon, Keyboard as KeyboardIcon,
  OpenInNew as NavigateIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { STUDENT } from '../sharedMockData';

/**
 * Ext Admin — Recording state
 *
 * Staff / developer-facing authoring UI. End-users never see this.
 * Shows recording dot + timer, live step list that grows as user acts,
 * and the "Analyzing recorded actions…" overlay state from Swarm 4b's
 * RecordingAnalysisOverlay.
 */

const SIDEBAR_WIDTH = 400;
const PURPLE = '#7b1fa2';
const RED = '#e53935';

const RECORDED_STEPS_BASE = [
  { num: 1, type: 'navigate', name: 'Navigate to khanacademy.org/login', t: '0:02', icon: <NavigateIcon sx={{ fontSize: 14 }} /> },
  { num: 2, type: 'click',    name: 'Click #uid-identifier-input',        t: '0:05', icon: <MouseIcon sx={{ fontSize: 14 }} /> },
  { num: 3, type: 'type',     name: `Type "${STUDENT.khanUser}"`,          t: '0:08', icon: <KeyboardIcon sx={{ fontSize: 14 }} /> },
  { num: 4, type: 'click',    name: 'Click button[data-test-id="identifier-submit"]', t: '0:11', icon: <MouseIcon sx={{ fontSize: 14 }} /> },
  { num: 5, type: 'type',     name: 'Type password (masked)',             t: '0:14', icon: <KeyboardIcon sx={{ fontSize: 14 }} /> },
];

const TYPE_COLORS = {
  navigate: { bg: '#e3f2fd', fg: '#1565c0' },
  click:    { bg: '#e3f2fd', fg: '#1565c0' },
  type:     { bg: '#e3f2fd', fg: '#1565c0' },
  wait:     { bg: '#fff3e0', fg: '#e65100' },
};

const ExtAdminRecordingMockup = () => {
  const [elapsed, setElapsed] = useState(14);
  const [analyzing, setAnalyzing] = useState(false);
  const [blinkOn, setBlinkOn] = useState(true);

  useEffect(() => {
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setBlinkOn(b => !b), 700);
    return () => clearInterval(t);
  }, []);

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(1, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#eef1f5', p: 3, minHeight: 'calc(100vh - 180px)' }}>
      <Paper elevation={6} sx={{
        width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column',
        bgcolor: '#fff', overflow: 'hidden', position: 'relative',
        border: '1px solid #d5d9df', maxHeight: 'calc(100vh - 200px)',
      }}>
        {/* Toolbar (unchanged) */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          bgcolor: PURPLE, color: '#fff',
        }}>
          <ExtensionIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Automatrix</Typography>
          <Chip label="v5.0.0" size="small" sx={{
            height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.18)',
            color: '#fff', fontWeight: 600,
          }} />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" sx={{ color: '#fff' }}>
            <SettingsIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" sx={{ color: '#fff' }}>
            <MoreIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Recording status banner (prominent) */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          bgcolor: blinkOn ? '#ffebee' : '#ffcdd2',
          borderBottom: `2px solid ${RED}`,
          transition: 'background-color 0.3s',
        }}>
          <RecordIcon sx={{
            fontSize: 18, color: RED,
            opacity: blinkOn ? 1 : 0.3, transition: 'opacity 0.3s',
          }} />
          <Typography sx={{ fontWeight: 700, color: RED, fontSize: '0.85rem', flexGrow: 1 }}>
            Recording… {mmss}
          </Typography>
          <Chip
            label={`${RECORDED_STEPS_BASE.length} steps`}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#fff', color: RED, fontWeight: 700 }}
          />
          <Tooltip title="Stop and analyze">
            <IconButton
              size="small"
              onClick={() => setAnalyzing(true)}
              sx={{
                bgcolor: RED, color: '#fff', width: 26, height: 26,
                '&:hover': { bgcolor: '#c62828' },
              }}
            >
              <StopIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* AI hint */}
        <Alert
          severity="info"
          icon={<AiIcon sx={{ fontSize: 16 }} />}
          sx={{
            mx: 1, mt: 1, py: 0.25, fontSize: '0.72rem',
            '& .MuiAlert-message': { py: 0.25 },
            '& .MuiAlert-icon': { py: 0.25, mr: 1 },
          }}
        >
          AI will rank selectors after stop. Navigate, click, and type as normal.
        </Alert>

        {/* Live step list */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', px: 0.75, py: 0.75 }}>
          <Typography variant="caption" sx={{
            px: 1, py: 0.5, display: 'block', color: '#607d8b',
            fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
          }}>
            Recorded actions (live)
          </Typography>

          {RECORDED_STEPS_BASE.map((step, i) => {
            const tc = TYPE_COLORS[step.type] || TYPE_COLORS.click;
            const isNewest = i === RECORDED_STEPS_BASE.length - 1;
            return (
              <Box key={step.num} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.55,
                borderRadius: 1, mb: 0.4,
                bgcolor: isNewest ? '#fff8e1' : '#fafafa',
                border: isNewest ? '1px solid #ffc107' : '1px solid #eee',
                animation: isNewest ? 'slideIn 0.4s ease-out' : 'none',
                '@keyframes slideIn': {
                  from: { opacity: 0, transform: 'translateX(-6px)' },
                  to:   { opacity: 1, transform: 'translateX(0)' },
                },
              }}>
                <Box sx={{ color: '#757575' }}>{step.icon}</Box>
                <Typography variant="caption" sx={{
                  fontWeight: 700, color: '#546e7a', minWidth: 14, fontSize: '0.7rem',
                }}>
                  {step.num}
                </Typography>
                <Chip label={step.type} size="small" sx={{
                  height: 16, fontSize: '0.58rem', bgcolor: tc.bg,
                  color: tc.fg, fontWeight: 700, textTransform: 'capitalize',
                }} />
                <Typography variant="body2" sx={{
                  flexGrow: 1, fontSize: '0.76rem',
                  fontFamily: step.type === 'click' || step.type === 'type' ? 'monospace' : 'inherit',
                }}>
                  {step.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#90a4ae', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                  {step.t}
                </Typography>
              </Box>
            );
          })}

          {/* "capturing next action" pulse row */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.6,
            borderRadius: 1, mb: 0.4, border: '1px dashed #b39ddb',
            bgcolor: '#faf7fd',
          }}>
            <CircularProgress size={12} sx={{ color: PURPLE }} />
            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: PURPLE, fontStyle: 'italic' }}>
              Listening for next action…
            </Typography>
          </Box>
        </Box>

        {/* Transport bar (Record active) */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75,
          borderTop: '1px solid #eceff2', bgcolor: '#fff',
        }}>
          <IconButton size="small" disabled sx={{ width: 28, height: 28 }}>
            <PlayIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" disabled sx={{ width: 28, height: 28 }}>
            <StepIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" disabled sx={{ width: 28, height: 28 }}>
            <PauseIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" disabled sx={{ width: 28, height: 28 }}>
            <StopIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Stop recording">
            <IconButton
              size="small"
              onClick={() => setAnalyzing(true)}
              sx={{
                bgcolor: RED, color: '#fff', width: 28, height: 28,
                '&:hover': { bgcolor: '#c62828' },
                animation: 'pulse 1.2s infinite',
                '@keyframes pulse': {
                  '0%':   { boxShadow: '0 0 0 0 rgba(229, 57, 53, 0.6)' },
                  '70%':  { boxShadow: '0 0 0 6px rgba(229, 57, 53, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(229, 57, 53, 0)' },
                },
              }}
            >
              <RecordIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: RED, fontWeight: 700 }}>
            LIVE
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
          borderTop: '1px solid #eceff2', bgcolor: '#f7f9fb',
        }}>
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#546e7a' }}>
            Network capture: off · Selector preview: on
          </Typography>
        </Box>

        {/* ANALYSIS OVERLAY (Swarm 4b RecordingAnalysisOverlay) */}
        {analyzing && (
          <Backdrop
            open
            sx={{
              position: 'absolute', zIndex: 10,
              bgcolor: 'rgba(20, 10, 40, 0.78)',
              display: 'flex', flexDirection: 'column', gap: 2,
              backdropFilter: 'blur(4px)',
            }}
          >
            <CircularProgress sx={{ color: '#ce93d8' }} />
            <Stack spacing={0.5} alignItems="center">
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                Analyzing recorded actions…
              </Typography>
              <Typography sx={{ color: '#e1bee7', fontSize: '0.75rem', textAlign: 'center', maxWidth: 260 }}>
                Ranking 12 selector strategies per step · AI-naming steps · deduplicating redundant waits
              </Typography>
            </Stack>
            <Box sx={{ width: '70%', mt: 1 }}>
              <LinearProgress sx={{
                height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)',
                '& .MuiLinearProgress-bar': { bgcolor: '#ce93d8' },
              }} />
              <Typography sx={{ color: '#bbb', fontSize: '0.68rem', mt: 0.5, textAlign: 'center' }}>
                Step 3 of 5 · POST /automations/generate/rank-selectors
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setAnalyzing(false)}
              sx={{
                position: 'absolute', top: 8, right: 8,
                color: '#fff', bgcolor: 'rgba(255,255,255,0.1)',
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Backdrop>
        )}
      </Paper>
    </Box>
  );
};

export default ExtAdminRecordingMockup;
